import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { useAuth } from '@/App';
import { createProductReview, getProductReviews, getFarmers, getProducts, trackAnalyticsEvent } from '@/lib/api';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Leaf,
  MapPin,
  Plus,
  Star,
  User,
  X,
} from 'lucide-react';

const StarRating = ({ rating = 0 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-4 h-4 ${
          star <= Math.round(rating)
            ? 'fill-yellow-400 text-yellow-400'
            : 'fill-gray-200 text-gray-200'
        }`}
      />
    ))}
  </div>
);

const ProductImageCarousel = ({ product }) => {
  const images = product.image_urls?.length
    ? product.image_urls
    : product.image_url
      ? [product.image_url]
      : [];
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    setImageIndex(0);
  }, [product.slug, images.length]);

  if (!images.length) {
    return (
      <div className="w-full h-48 bg-muted flex items-center justify-center text-muted-foreground">
        Product image
      </div>
    );
  }

  const moveImage = (event, direction) => {
    event.stopPropagation();
    setImageIndex((current) =>
      (current + direction + images.length) % images.length
    );
  };

  return (
    <div className="relative bg-white">
      <img
        src={images[imageIndex]}
        alt={`${product.name} ${imageIndex + 1}`}
        className="w-full h-48 object-contain bg-white p-2"
      />

      {images.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous product image"
            onClick={(event) => moveImage(event, -1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/95 border border-border shadow-sm flex items-center justify-center text-foreground hover:bg-white"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            type="button"
            aria-label="Next product image"
            onClick={(event) => moveImage(event, 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/95 border border-border shadow-sm flex items-center justify-center text-foreground hover:bg-white"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, index) => (
              <span
                key={index}
                className={`w-1.5 h-1.5 rounded-full ${
                  index === imageIndex ? 'bg-primary' : 'bg-primary/25'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { addToCart } = useAuth();

  const [products, setProducts] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileFarmer, setProfileFarmer] = useState(null);
  const [profilePhotoIndex, setProfilePhotoIndex] = useState(0);
  const profilePhotoStripRef = useRef(null);
  const [reviewProduct, setReviewProduct] = useState(null);
  const [viewReviewsProduct, setViewReviewsProduct] = useState(null);
  const [visibleReviews, setVisibleReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    reviewer_name: '',
    customer_phone: '',
    rating: 0,
    review_text: '',
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    trackAnalyticsEvent('PAGE_VIEW').catch(() => {});

    const load = async () => {
      try {
        const [productsRes, farmersRes] = await Promise.all([
          getProducts(),
          getFarmers(),
        ]);
        setProducts(productsRes.data);
        setFarmers(farmersRes.data);
      } catch (error) {
        console.error('Failed to load marketplace:', error);
      } finally {
        setLoading(false);
      }
    };

    load();

    const refresh = setInterval(async () => {
      try {
        const productsRes = await getProducts();
        setProducts(productsRes.data);
      } catch (error) {
        console.error('Failed to refresh products:', error);
      }
    }, 10000);

    return () => clearInterval(refresh);
  }, []);

  const farmerMap = useMemo(
    () =>
      farmers.reduce((map, farmer) => {
        map[farmer.slug] = farmer;
        return map;
      }, {}),
    [farmers]
  );

  const filteredProducts = products;

  const farmerPhotos = (farmer) =>
    [...new Set([farmer?.photo_url, ...(farmer?.photo_urls || [])].filter(Boolean))].slice(0, 2);

  const profileFarmerPhotos = profileFarmer ? farmerPhotos(profileFarmer) : [];

  useEffect(() => {
    setProfilePhotoIndex(0);
    if (profilePhotoStripRef.current) {
      profilePhotoStripRef.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [profileFarmer]);

  const moveProfilePhoto = (event, direction) => {
    event.stopPropagation();
    if (profileFarmerPhotos.length < 2) return;

    const nextIndex =
      (profilePhotoIndex + direction + profileFarmerPhotos.length) %
      profileFarmerPhotos.length;

    setProfilePhotoIndex(nextIndex);

    const strip = profilePhotoStripRef.current;
    if (strip) {
      strip.scrollTo({
        left: strip.clientWidth * nextIndex,
        behavior: 'smooth',
      });
    }
  };

  const handleProfilePhotoScroll = () => {
    const strip = profilePhotoStripRef.current;
    if (!strip || !strip.clientWidth) return;
    const nextIndex = Math.round(strip.scrollLeft / strip.clientWidth);
    if (nextIndex !== profilePhotoIndex) {
      setProfilePhotoIndex(nextIndex);
    }
  };

  const openReviewForm = (event, product) => {
    event.stopPropagation();
    setReviewProduct(product);
    setReviewForm({
      reviewer_name: '',
      customer_phone: '',
      rating: 0,
      review_text: '',
    });
  };

  const openReviews = async (event, product) => {
    event.stopPropagation();
    setViewReviewsProduct(product);
    setVisibleReviews([]);
    setReviewsLoading(true);
    try {
      const response = await getProductReviews(product.slug);
      setVisibleReviews(response.data?.reviews || []);
    } catch (error) {
      toast.error('Unable to load customer reviews');
    } finally {
      setReviewsLoading(false);
    }
  };

  const submitHomeReview = async () => {
    if (!reviewProduct) return;
    if (!reviewForm.reviewer_name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!reviewForm.customer_phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (!reviewForm.rating) {
      toast.error('Please select a star rating');
      return;
    }
    if (reviewForm.review_text.trim().length < 2) {
      toast.error('Please add a short review');
      return;
    }

    setSubmittingReview(true);
    try {
      const response = await createProductReview(reviewProduct.slug, reviewForm);
      toast.success(
        response.data?.verified_purchase
          ? 'Thanks — your review is marked Verified Purchase'
          : 'Thanks for your review'
      );
      setReviewProduct(null);
      const productsRes = await getProducts();
      setProducts(productsRes.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleQuickBuy = (event, product) => {
    event.stopPropagation();
    if (product.inventory_available != null && Number(product.inventory_available) <= 0) return;
    addToCart(
      {
        ...product,
        farmer: farmerMap[product.farmer_slug],
      },
      1,
      true
    );
    toast.success(`Added ${product.name} to cart`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <section className="bg-gradient-to-b from-primary/5 to-transparent pt-8 md:pt-12 pb-3 md:pb-4">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="heading-1 text-3xl md:text-5xl max-w-3xl mx-auto leading-tight mb-4">
            Direct Staples from <span className="text-secondary">Farmers</span>
          </h1>

          <div className="max-w-5xl mx-auto">
            <p className="text-base md:text-lg font-medium text-foreground/90">
              Empower farmers to sell direct, under their own name.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {[
                ['Tested', 'Produce is tested'],
                ['Farmer', 'You know your farmer'],
                ['Origin', 'You know where your food comes from'],
                ['Transparent', 'You know what the farmer earns'],
              ].map(([label, text]) => (
                <div
                  key={label}
                  className="group relative overflow-hidden rounded-2xl border border-primary/10 bg-white/85 px-5 py-6 text-left shadow-[0_8px_30px_rgba(31,78,53,0.06)] transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-[0_12px_34px_rgba(31,78,53,0.10)]"
                >
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-secondary via-secondary/70 to-primary/70" />
                  <p className="text-[11px] uppercase tracking-[0.20em] text-secondary font-semibold">
                    {label}
                  </p>
                  <p className="mt-2 text-base md:text-lg font-semibold leading-snug text-foreground">
                    {text}
                  </p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      <section id="products-section" className="max-w-6xl mx-auto px-4 pt-3 md:pt-4 pb-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((item) => (
              <Card key={item} className="animate-pulse">
                <div className="h-48 bg-muted rounded-t-xl" />
                <CardContent className="p-4 space-y-2">
                  <div className="h-5 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <Card>
            <CardContent className="py-14 text-center text-muted-foreground">
              No products match your search.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => {
              const farmer = farmerMap[product.farmer_slug];
              const firstVariant = product.variants?.[0];
              const packaging = Number(
                firstVariant?.packaging ??
                firstVariant?.handling ??
                product.packaging ??
                product.handling ??
                0
              );
              const totalPrice =
                Number(firstVariant?.list_price) ||
                Number(product.list_price) ||
                (
                  Number(firstVariant?.total_price ?? product.total_price) +
                  Number((firstVariant?.platform_fee ?? product.platform_fee) || 0)
                ) ||
                Number((firstVariant?.farmer_price ?? product.farmer_price) || 0) +
                  Number((firstVariant?.platform_fee ?? product.platform_fee) || 0) +
                  Number((firstVariant?.shipping ?? product.shipping) || 0) +
                  packaging +
                  Number((firstVariant?.testing ?? product.testing) || 0);

              return (
                <Card
                  key={product.slug}
                  className="card-hover overflow-hidden cursor-pointer h-full flex flex-col"
                  onClick={() => navigate(`/product/${product.slug}`)}
                >
                  <div className="relative">
                    <ProductImageCarousel product={product} />

                    {product.category && (
                      <Badge className="absolute top-3 left-3 bg-card/90 text-foreground">
                        {product.category}
                      </Badge>
                    )}
                  </div>

                  <CardContent className="p-4 flex flex-col flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-lg leading-tight">
                        {product.name}
                      </h3>

                      <div className="text-right shrink-0 ml-2">
                        <p className="text-xl font-bold text-primary">
                          {(product.variants?.length || 0) > 1 ? 'From ' : ''}₹{totalPrice}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(product.variants?.length || 0) > 1
                            ? product.variants.map((variant) => variant.pack_size).join(' / ')
                            : product.pack_size}
                        </p>
                      </div>
                    </div>

                    {product.description && (
                      <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3" aria-label={`${product.name} highlights`}>
                        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-emerald-900">Product highlights</p>
                        <ul className="space-y-2">
                          {product.description
                            .split(/(?=[🌴🥥🌱🪴🍯✨🌾🌿🍃🌶️💚⭐💪])/u)
                            .flatMap((part) => part.split(/\s*[;|]\s*|\r?\n/))
                            .map((part) => part.trim())
                            .filter(Boolean)
                            .map((highlight, index) => (
                              <li key={index} className="text-sm leading-6 text-slate-800 break-words">
                                {highlight}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}

                    <div className="flex items-center justify-between gap-3 mb-3 mt-auto">
                      <button
                        type="button"
                        onClick={(event) => openReviews(event, product)}
                        className="flex items-center gap-2 rounded-md hover:bg-muted/50 px-1 py-1 -ml-1 transition-colors"
                        aria-label={`Read customer reviews for ${product.name}`}
                      >
                        <StarRating rating={product.rating || 0} />
                        <span className="text-sm font-semibold text-foreground">
                          {product.review_count > 0
                            ? `${product.rating} (${product.review_count})`
                            : 'Be the first to review'}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={(event) => openReviewForm(event, product)}
                        className="shrink-0 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors"
                      >
                        Write a review
                      </button>
                    </div>

                    <div
                      className="flex items-center gap-3 py-3 border-t border-b border-border/50 mb-3 rounded-md hover:bg-muted/40 transition-colors cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (farmer) setProfileFarmer(farmer);
                      }}
                      onKeyDown={(event) => {
                        if ((event.key === 'Enter' || event.key === ' ') && farmer) {
                          event.preventDefault();
                          event.stopPropagation();
                          setProfileFarmer(farmer);
                        }
                      }}
                    >
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center">
                        {farmerPhotos(farmer)[0] ? (
                          <img
                            src={farmerPhotos(farmer)[0]}
                            alt={farmer.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-4 h-4 text-primary" />
                        )}
                      </div>

                      <div className="text-sm flex-1">
                        <p className="font-medium">
                          {farmer?.name || product.farmer_slug}
                        </p>
                        {(farmer?.city || farmer?.village || farmer?.district) && (
                          <p className="text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {[farmer?.city || farmer?.village, farmer?.district]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                        )}
                      </div>


                    </div>

                    <div className="flex items-center justify-between text-sm">
                      {product.inventory_available != null && Number(product.inventory_available) <= 0 ? (
                        <span className="inline-flex items-center rounded-md bg-[#f7e9e9] px-2.5 py-1 text-xs font-semibold tracking-wide text-[#803b48]">
                          OUT OF STOCK
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          {product.inventory_available != null
                            ? `${product.inventory_available} ${product.inventory_unit} available`
                            : 'Available'}
                        </span>
                      )}

                      <Button
                        size="sm"
                        onClick={(event) => handleQuickBuy(event, product)}
                        disabled={product.inventory_available != null && Number(product.inventory_available) <= 0}
                        className="btn-pill bg-primary text-primary-foreground text-xs px-3 h-8 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed disabled:opacity-100"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {viewReviewsProduct && (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={() => setViewReviewsProduct(null)}>
          <div role="dialog" aria-modal="true" aria-label={`Customer reviews for ${viewReviewsProduct.name}`} className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-background shadow-2xl p-6" onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => setViewReviewsProduct(null)} className="absolute top-4 right-4 w-9 h-9 rounded-full border border-border flex items-center justify-center" aria-label="Close customer reviews"><X className="w-5 h-5" /></button>
            <h2 className="text-2xl font-semibold pr-12">Customer Reviews</h2>
            <p className="text-muted-foreground mt-1 mb-5">{viewReviewsProduct.name}</p>
            {reviewsLoading ? <p>Loading reviews…</p> : visibleReviews.length === 0 ? <p className="text-muted-foreground">No customer reviews yet.</p> : (
              <div className="divide-y divide-border">
                {visibleReviews.map((review) => (
                  <article key={review.review_id} className="py-5 first:pt-0">
                    <p className="font-medium">{review.reviewer_name || 'Customer'}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <StarRating rating={review.rating} />
                      {review.verified_purchase && <span className="inline-flex items-center rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-800">✓ Verified Purchase</span>}
                    </div>
                    {review.created_at && <p className="text-xs text-muted-foreground mt-1">Reviewed on {new Date(review.created_at).toLocaleDateString('en-IN', {day:'numeric',month:'long',year:'numeric'})}</p>}
                    <p className="mt-3 whitespace-pre-wrap leading-relaxed">{review.review_text}</p>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {reviewProduct && (
        <div
          className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4"
          onClick={() => setReviewProduct(null)}
        >
          <div
            className="relative w-full max-w-md rounded-2xl bg-background shadow-2xl p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setReviewProduct(null)}
              className="absolute top-4 right-4 w-9 h-9 rounded-full border border-border bg-background flex items-center justify-center"
              aria-label="Close review form"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-xl font-bold pr-10">Review {reviewProduct.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Your phone number is checked against 0middle orders to identify a verified purchase.
            </p>

            <div className="mt-5">
              <p className="text-sm font-medium mb-2">Your rating</p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setReviewForm((current) => ({ ...current, rating: value }))}
                    aria-label={`Rate ${value} stars`}
                  >
                    <Star
                      className={`w-8 h-8 ${
                        value <= reviewForm.rating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <Input
                value={reviewForm.reviewer_name}
                onChange={(event) =>
                  setReviewForm((current) => ({ ...current, reviewer_name: event.target.value }))
                }
                placeholder="Your name"
                maxLength={120}
              />
              <Input
                value={reviewForm.customer_phone}
                onChange={(event) =>
                  setReviewForm((current) => ({ ...current, customer_phone: event.target.value }))
                }
                placeholder="Phone number used for your order"
                inputMode="tel"
              />
              <textarea
                value={reviewForm.review_text}
                onChange={(event) =>
                  setReviewForm((current) => ({ ...current, review_text: event.target.value }))
                }
                placeholder="Share your experience"
                maxLength={2000}
                rows={4}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>

            <Button
              type="button"
              className="w-full mt-4"
              onClick={submitHomeReview}
              disabled={submittingReview}
            >
              {submittingReview ? 'Submitting…' : 'Submit review'}
            </Button>
          </div>
        </div>
      )}

      {profileFarmer && (
        <div
          className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
          onClick={() => setProfileFarmer(null)}
          role="presentation"
        >
          <div
            className="relative bg-card rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Meet ${profileFarmer.name}`}
          >
            <button
              type="button"
              onClick={() => setProfileFarmer(null)}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-background/90 border flex items-center justify-center hover:bg-muted"
              aria-label="Close farmer profile"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="grid md:grid-cols-2">
              <div className="relative bg-muted overflow-hidden">
                {profileFarmerPhotos.length > 0 ? (
                  <>
                    <div
                      ref={profilePhotoStripRef}
                      onScroll={handleProfilePhotoScroll}
                      className="flex w-full overflow-x-auto snap-x snap-mandatory scroll-smooth"
                      style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      {profileFarmerPhotos.map((imageUrl, index) => (
                        <div
                          key={`${imageUrl}-${index}`}
                          className="w-full min-w-full snap-center flex items-center justify-center bg-muted"
                        >
                          <img
                            src={imageUrl}
                            alt={`${profileFarmer.name} ${index + 1}`}
                            className="block w-full h-auto object-contain"
                          />
                        </div>
                      ))}
                    </div>

                    {profileFarmerPhotos.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={(event) => moveProfilePhoto(event, -1)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/75 border border-white/60 shadow-sm flex items-center justify-center text-foreground/75 hover:bg-white/90"
                          aria-label="Previous farmer photo"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>

                        <button
                          type="button"
                          onClick={(event) => moveProfilePhoto(event, 1)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/75 border border-white/60 shadow-sm flex items-center justify-center text-foreground/75 hover:bg-white/90"
                          aria-label="Next farmer photo"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>

                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5 rounded-full bg-black/20 px-2 py-1">
                          {profileFarmerPhotos.map((_, index) => (
                            <span
                              key={index}
                              className={`w-1.5 h-1.5 rounded-full ${index === profilePhotoIndex ? 'bg-white' : 'bg-white/50'}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full min-h-[260px] md:min-h-[480px] flex items-center justify-center">
                    <User className="w-20 h-20 text-muted-foreground/40" />
                  </div>
                )}
              </div>

              <div className="p-6 md:p-10 flex flex-col justify-center">
                {profileFarmer.headline && (
                  <h2 className="text-xl md:text-2xl font-extrabold leading-tight mb-5 text-foreground">
                    {profileFarmer.headline}
                  </h2>
                )}
                {profileFarmer.story && (
                  <div className="text-sm md:text-base leading-7 text-foreground/80 whitespace-pre-line">
                    {profileFarmer.story}
                  </div>
                )}
                {(profileFarmer.village || profileFarmer.city || profileFarmer.district || profileFarmer.state) && (
                  <div className="mt-6 pt-4 border-t flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    {[profileFarmer.city || profileFarmer.village, profileFarmer.district, profileFarmer.state]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="bg-primary text-primary-foreground py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img
              src="/0middle-logo.svg"
              alt="0middle logo"
              className="w-8 h-8 object-contain brightness-0 invert"
            />
            <h3
              className="text-xl font-bold"
              style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              <span
                style={{ fontFamily: "'SF Mono', Menlo, Consolas, monospace", fontVariantNumeric: 'slashed-zero', fontFeatureSettings: "'zero' 1" }}
              >
                0
              </span>
              middle
            </h3>
          </div>
          <p className="text-primary-foreground/80 text-sm">
            Infrastructure for farm-direct commerce. We don't own products or
            set prices.
          </p>

          <p className="text-primary-foreground/80 text-sm mt-3">
            Contact us: <a href="mailto:info@0middle.com" className="underline underline-offset-2">info@0middle.com</a> · <a href="tel:+917204438810" className="underline underline-offset-2">+91 72044 38810</a>
          </p>

        </div>
      </footer>
    </div>
  );
};

export default HomePage;
